/**
 * The next free employee number, read off the ones a company already uses.
 *
 * There is no server call for this: `employee_number` is a free-form string the
 * operator owns, and sv_control neither issues nor validates it — it only
 * refuses a duplicate, with the 409 the form already turns into a message at
 * the field. So the button cannot ask for the next number; it has to work one
 * out from the roster and let that 409 stay as the backstop.
 *
 * ## Why a prefix is kept
 *
 * Companies number people `104`, `0007`, `EMP-014` and `Ц-12`, and they do it
 * consistently — the shape IS the convention. Generating a bare `105` into a
 * roster of `EMP-*` would quietly start a second convention, and the operator
 * would be the one to notice, months later, in an export. So the counter is the
 * trailing run of digits and everything before it travels unchanged.
 *
 * Zero padding is kept for the same reason: a roster of `0007…0099` continues
 * `0100`, not `100`. It stops being kept only when the count outgrows the pad —
 * `099 → 100` — because dropping a digit is not an option and silently widening
 * everything else is not ours to do.
 *
 * ## What it does with a mess
 *
 * A roster that mixes conventions gets the one that is most used, ties broken by
 * the higher counter — that is the live convention, and the others are history
 * somebody has already stopped writing. A roster with no digits anywhere at all
 * (or an empty one) starts at `1`: any other seed would be this file inventing a
 * company's numbering scheme.
 */

/** Longer than this is an identifier, not a counter — `Number` stops being exact. */
const MAX_COUNTER_DIGITS = 15;

/** Anything ending in digits: the run at the end counts, the rest repeats. */
const TRAILING_DIGITS = /^(.*?)(\d+)$/;

type Convention = {
  /** Everything before the counter — `''`, `'EMP-'`, `'Ц-'`. */
  prefix: string;
  /** How many people are numbered this way. The most-used convention wins. */
  count: number;
  /** The highest counter seen under this prefix. */
  max: number;
  /** The widest counter seen, so `0007` continues `0008` and not `8`. */
  width: number;
};

function readConventions(existing: readonly string[]): Map<string, Convention> {
  const byPrefix = new Map<string, Convention>();

  for (const raw of existing) {
    const value = raw?.trim();
    if (!value) {
      continue;
    }
    const parts = TRAILING_DIGITS.exec(value);
    if (!parts) {
      continue;
    }
    const [, prefix, digits] = parts;
    if (digits.length > MAX_COUNTER_DIGITS) {
      continue;
    }

    const counter = Number(digits);
    const seen = byPrefix.get(prefix);
    if (seen) {
      seen.count += 1;
      seen.max = Math.max(seen.max, counter);
      seen.width = Math.max(seen.width, digits.length);
    } else {
      byPrefix.set(prefix, {prefix, count: 1, max: counter, width: digits.length});
    }
  }

  return byPrefix;
}

/** The most-used convention; ties go to the one that has counted higher. */
function liveConvention(byPrefix: Map<string, Convention>): Convention | null {
  let best: Convention | null = null;
  for (const c of byPrefix.values()) {
    if (
      !best ||
      c.count > best.count ||
      (c.count === best.count && c.max > best.max) ||
      // Deterministic to the end: two conventions used equally, counted equally,
      // must still produce the same suggestion on every render.
      (c.count === best.count && c.max === best.max && c.prefix < best.prefix)
    ) {
      best = c;
    }
  }
  return best;
}

function format(convention: Convention, counter: number): string {
  return convention.prefix + String(counter).padStart(convention.width, '0');
}

/**
 * The number to offer for a new person.
 *
 * Never returns one that is already in `existing`: the roster can hold a gap
 * ABOVE the counter (an import, a hand-typed `500` among `104…106`), and
 * handing back a taken number would turn a convenience into a 409.
 */
export function nextEmployeeNumber(existing: readonly string[]): string {
  const taken = new Set(existing.map(v => v?.trim()).filter(Boolean));
  const convention = liveConvention(readConventions(existing));

  if (!convention) {
    // Nothing numbered yet. `1` is the only seed that is not an invention.
    return taken.has('1') ? '2' : '1';
  }

  let counter = convention.max + 1;
  let candidate = format(convention, counter);
  // Bounded: the roster is finite, so at most one step per person can be taken.
  for (let guard = 0; taken.has(candidate) && guard <= taken.size; guard += 1) {
    counter += 1;
    candidate = format(convention, counter);
  }
  return candidate;
}
